import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { TID } from "@/lib/testIds";
import TaskCheck from "@/components/TaskCheck";
import DeadlineBadge from "@/components/DeadlineBadge";
import ProgressBar from "@/components/ProgressBar";
import CountryPostArrival from "@/components/CountryPostArrival";
import InrAmount from "@/components/InrAmount";
import WaitTimeReportPrompt from "@/components/WaitTimeReportPrompt";
import OnboardingWalkthrough from "@/components/OnboardingWalkthrough";
import PersonaQuestionnaire from "@/components/PersonaQuestionnaire";
import ProductTour from "@/components/ProductTour";
import DeleteAccountSection from "@/components/DeleteAccountSection";
import NotificationPrefsToggle from "@/components/NotificationPrefsToggle";
import DashboardOverview from "@/components/DashboardOverview";
import PassiveMatchCard from "@/components/PassiveMatchCard";
import { productOrder, sectionFraming, personaSummary } from "@/lib/personaLayout";
import {
  FileCheck, Wallet, Home as HomeIcon, ShieldCheck, Train, MapPin, Landmark,
  AlertTriangle, FolderOpen, Users, Compass, GraduationCap, ArrowRight, Package,
  Sparkles, Map, Coins, LifeBuoy,
} from "lucide-react";

const STAGE_LABEL = {
  pre_departure: { label: "Before you fly",  panel: "moe-panel-cyan" },
  arrival:       { label: "First 12 days",   panel: "moe-panel-yolk" },
  settling:      { label: "Settling in",     panel: "moe-panel-mint" },
};
const CAT_ICON = {
  visa: FileCheck, funds: Wallet, accommodation: HomeIcon, insurance: ShieldCheck,
  transit: Train, connectivity: Train, registration: Landmark, finance: Wallet,
  documents: FolderOpen, admission: MapPin, travel: MapPin, settling: HomeIcon,
  academic: Landmark, social: Users,
};

export default function Dashboard() {
  const [state, setState] = useState(null);
  const [manifest, setManifest] = useState(null);
  const [packing, setPacking]   = useState(null);
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  // iter71 — Persona questionnaire modal state. Auto-opens on FIRST dashboard
  // visit after a case exists (persona == null AND persona_skipped == false).
  // Shown BEFORE the OnboardingWalkthrough so we know the persona before the
  // dashboard renders its persona-driven ordering.
  const [personaOpen, setPersonaOpen] = useState(false);
  // iter75 (Item B) — Product tour modal state. Auto-fires on first dashboard
  // visit AFTER persona is answered or skipped. product_tour_seen defaults
  // to false server-side on case create; X/Esc/backdrop/Finish all persist
  // product_tour_seen=true so it never re-prompts.
  const [productTourOpen, setProductTourOpen] = useState(false);
  // iter56 — vendor category count for the quiet "you skipped the tour" nudge.
  // Only fetched when the case's `onboarding_walkthrough_outcome === "skipped"`
  // so completers and first-time users pay zero extra network cost.
  const [vendorCatCount, setVendorCatCount] = useState(null);
  const load = () => api.get("/cases/me").then(r => {
    setState(r.data);
    if (r.data?.case) {
      // Only fetch these once we know the user has a case — avoids 400 console noise on the "Let's start" screen.
      api.get("/manifest/summary").then(res => setManifest(res.data)).catch(() => setManifest({opted_in: false}));
      api.get("/packing/summary").then(res => setPacking(res.data)).catch(() => setPacking(null));
      // iter75 (Item B) — Chain on first-time-user modal cascade:
      //   1. Persona questionnaire (iter71). If persona is null and not
      //      persona_skipped, opens first.
      //   2. Product tour (this iteration). Auto-opens if
      //      product_tour_seen === false and the user has already resolved
      //      the persona (either answered or skipped it).
      //   3. Vendor walkthrough (iter55). No longer auto-fires. Users open it
      //      on demand from the dashboard footer via the same
      //      "Review setup options" link (existing wiring at bottom of page).
      const needsPersona = r.data.case.persona == null && r.data.case.persona_skipped !== true;
      const needsProductTour = r.data.case.product_tour_seen === false && !needsPersona;
      if (needsPersona) {
        setPersonaOpen(true);
      } else if (needsProductTour) {
        setProductTourOpen(true);
      }
      // iter56 — quiet nudge: fetch vendor category count only if outcome is
      // "skipped" (not "completed", not first-visit-not-yet-seen).
      if (r.data.case.onboarding_walkthrough_outcome === "skipped") {
        api.get(`/countries/${encodeURIComponent(r.data.case.country)}/vendor-links`)
          .then(res => setVendorCatCount((res.data?.categories || []).length))
          .catch(() => setVendorCatCount(null));
      } else {
        setVendorCatCount(null);
      }
    }
  });
  useEffect(() => { load(); }, []);

  if (!state) return (
    <div className="max-w-5xl mx-auto px-6 py-12">
      <div className="moe-shimmer h-8 w-64 mb-4"/>
      <div className="grid md:grid-cols-3 gap-4">{Array.from({length:6}).map((_,i)=><div key={i} className="moe-shimmer h-24"/>)}</div>
    </div>
  );

  if (!state.case) return (
    <div className="max-w-2xl mx-auto px-6 pt-24 pb-24 text-center">
      <Compass size={48} className="text-elec mx-auto moe-float" strokeWidth={1.4}/>
      <h1 className="mt-6 text-3xl font-bold tracking-tight">Let's start.</h1>
      <p className="mt-2 text-muted">Five short questions and your Northstar timeline is built.</p>
      <Link to="/start" className="mt-6 inline-flex moe-btn moe-btn-primary">Begin intake</Link>
    </div>
  );

  const { case: c, tasks, playbook } = state;
  const done = tasks.filter(t => t.status === "completed").length;
  const total = tasks.length || 1;
  const pctDone = Math.round((done / total) * 100);

  const grouped = { pre_departure: [], arrival: [], settling: [] };
  tasks.forEach(t => grouped[t.stage]?.push(t));

  const toggle = async (t) => {
    const next = t.status === "completed" ? "pending" : "completed";
    await api.patch(`/tasks/${t.id}`, { status: next });
    load();
  };

  const visa = c.visa_path || {};

  // iter71 — Persona-driven product ordering (Item 1). Reorders the Manifest /
  // Roam / Packing entry cards based on the user's answers. Falls back to a
  // neutral order when persona is null / skipped. Section framing ("what this
  // is for") stays honest across personas — the reorder carries the emphasis.
  const persona = c.persona || null;
  const order = productOrder(persona);
  // iter93 (Item GG · P1) — Real analytics (30d): Manifest + Roam get 68% + 23%
  // of dashboard viewers; Smart Packing gets ~1%. Keep only the two products
  // with actual demand in the top-fold strip. Packing (and Concierge below)
  // are still 100% reachable via nav and the "More tools" strip lower on the
  // page — this is reordering, not removal.
  const PRIMARY_STRIP_KEYS = ["manifest", "roam"];
  const primaryOrder = order.filter((k) => PRIMARY_STRIP_KEYS.includes(k));
  const framing = sectionFraming(persona);
  const personaLine = personaSummary(persona);

  // Build the three product-strip cards keyed by product. This is the "Product
  // sections" row that used to be split across the Manifest banner and the
  // Packing+Roam grid at the bottom of the page.
  const productCards = {
    manifest: manifest && (
      <Link
        to="/dashboard/manifest"
        data-testid="dash-manifest-banner"
        className="moe-card p-5 md:p-6 hover:border-elec transition-colors group block h-full"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-elec/10 text-elec flex items-center justify-center shrink-0">
            <Users size={18} strokeWidth={2}/>
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-[0.2em] text-elec font-bold">Cohort</div>
            <div className="mt-0.5 text-[13px] text-ink/70 leading-snug" data-testid="dash-manifest-framing">
              {framing.manifest.lede}
            </div>
            {manifest.opted_in ? (
              <div className="mt-2 text-sm md:text-base font-bold text-ink leading-snug" data-testid="dash-manifest-headline">
                {manifest.matches_count > 0
                  ? <>
                      {manifest.same_university_count > 0
                        ? <><span className="text-elec tabular-nums">{manifest.same_university_count}</span> student{manifest.same_university_count===1?"":"s"} matched at {c.host_university}</>
                        : manifest.same_city_count > 0
                          ? <><span className="text-elec tabular-nums">{manifest.same_city_count}</span> student{manifest.same_city_count===1?"":"s"} matched in {c.host_city}</>
                          : <><span className="text-elec tabular-nums">{manifest.matches_count}</span> student{manifest.matches_count===1?"":"s"} matched in {c.country}</>}
                      {manifest.pending_requests > 0 && <> · <span className="text-[color:var(--moe-pink)]">{manifest.pending_requests} pending</span></>}
                    </>
                  : <>No matches yet. You're the first one confirmed for your cohort.</>}
              </div>
            ) : (
              <div className="mt-2 text-sm md:text-base font-bold text-ink leading-snug" data-testid="dash-manifest-headline">
                Find your people before you land. Opt in to see who else is heading to {c.host_city}.
              </div>
            )}
            <div className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold text-elec">
              Open Cohort <ArrowRight size={13} className="transition-transform group-hover:translate-x-0.5"/>
            </div>
          </div>
        </div>
      </Link>
    ),
    roam: (
      <Link
        to="/dashboard/roam"
        data-testid="dash-roam-card"
        className="moe-card p-5 md:p-6 hover:border-elec transition-colors group block h-full moe-fade-up"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-elec/10 text-elec flex items-center justify-center shrink-0">
            <Map size={18} strokeWidth={2}/>
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-[0.2em] text-elec font-bold">Drift</div>
            <div className="mt-0.5 text-[13px] text-ink/70 leading-snug" data-testid="dash-roam-framing">
              {framing.roam.lede}
            </div>
            <div className="mt-2 text-sm md:text-base font-bold text-ink leading-snug">
              Weekend trips within ~300 km of {c.host_city || "your host city"}
            </div>
            <div className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold text-elec">
              Open Drift <ArrowRight size={13} className="transition-transform group-hover:translate-x-0.5"/>
            </div>
          </div>
        </div>
      </Link>
    ),
    packing: (
      <Link
        to="/dashboard/packing"
        data-testid="dash-packing-card"
        className="moe-card p-5 md:p-6 hover:border-elec transition-colors group block h-full moe-fade-up"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-elec/10 text-elec flex items-center justify-center shrink-0">
            <Package size={18} strokeWidth={2}/>
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-[0.2em] text-elec font-bold">Smart Packing</div>
            <div className="mt-0.5 text-[13px] text-ink/70 leading-snug" data-testid="dash-packing-framing">
              {framing.packing.lede}
            </div>
            <div className="mt-2 text-sm md:text-base font-bold text-ink leading-snug">
              {packing?.profile_complete
                ? <>{packing.readiness_pct}% ready · {packing.essential_remaining} essential left</>
                : <>Answer 6 quick questions to generate your list.</>}
            </div>
            <div className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold text-elec">
              Open Smart Packing <ArrowRight size={13} className="transition-transform group-hover:translate-x-0.5"/>
            </div>
          </div>
        </div>
      </Link>
    ),
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-10 pb-24">
      {/* HEADER */}
      <div className="grid md:grid-cols-3 gap-6 items-start">
        <div className="md:col-span-2">
          <div className="text-xs uppercase tracking-widest text-muted font-semibold">Your exchange</div>
          <h1 className="mt-2 text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight">
            {c.host_university}, <span className="text-elec">{c.country}</span>
          </h1>
          <div className="text-sm text-muted mt-2">
            {c.duration_days} days · starts {c.start_date} · from {c.home_university}
          </div>
          <div className="mt-6 flex items-center gap-3">
            <div className="flex-1"><ProgressBar value={done} max={total} testid={TID.dashProgress}/></div>
            <div className="text-sm font-bold text-ink tabular-nums">{done}/{total} · {pctDone}%</div>
          </div>
        </div>

        <div className="moe-panel-elec p-6 moe-hover-lift" data-testid={TID.dashVisaCard}>
          <div className="flex items-center gap-2 mb-2">
            <FileCheck size={16} strokeWidth={2} className="text-white"/>
            <div className="text-[10px] uppercase tracking-widest opacity-70 font-bold text-white">Visa path</div>
          </div>
          <div className="text-lg font-bold text-white">{visa.visa_type}</div>
          <div className="text-xs text-white/85 mt-2 leading-snug">{visa.message}</div>
          {visa.severity === "mandatory" && <div className="mt-3 moe-chip moe-chip-pink">Mandatory</div>}
          {visa.severity === "warn" && <div className="mt-3 moe-chip moe-chip-yolk">Boundary</div>}
          <Link to={`/countries/${encodeURIComponent(c.country)}`} className="mt-3 inline-block text-xs text-white/90 font-semibold hover:underline">See dossier →</Link>
        </div>
      </div>

      {/* iter101 — PASSIVE MATCH REVEAL. Replaces the "opt in to see peers"
         wall as the primary Cohort activation path. Renders only when the
         backend has ≥1 peer for this case; otherwise silent. Founder rule:
         additive-only, non-urgent, one primary action per state. */}
      <PassiveMatchCard hostCity={c.host_city} />

      {/* iter71. Persona-driven product strip. Manifest / Roam / Smart Packing
         entry cards, reordered based on the user's persona (traveler_type +
         primary_purpose). Neutral order when persona is null / skipped. Each
         card carries a "what this is for" framing line so a first-time viewer
         understands the purpose before scanning the data. */}
      <div
        className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4"
        data-testid="dash-product-strip"
        data-persona-order={primaryOrder.join(",")}
      >
        {primaryOrder.map((key) => (
          <div key={key} data-testid={`dash-product-slot-${key}`}>
            {productCards[key]}
          </div>
        ))}
      </div>

      {/* Personalisation summary + edit entry point. Renders only when persona
         is set. Quietly explains which sections are reordered and lets the
         user tweak or clear their answers. */}
      {personaLine && (
        <div
          className="mt-3 flex items-center justify-between gap-3 text-xs text-ink/70"
          data-testid="dash-persona-summary"
        >
          <div>
            <span className="uppercase tracking-widest font-bold text-ink/50 mr-2">Personalised for you</span>
            <span className="text-ink/80">{personaLine}</span>
          </div>
          <button
            type="button"
            data-testid="dash-persona-edit"
            onClick={() => setPersonaOpen(true)}
            className="underline underline-offset-2 font-semibold text-elec hover:text-ink"
          >
            Edit
          </button>
        </div>
      )}

      {/* iter56. Quiet nudge for students who explicitly skipped the guided
          walkthrough. Purely informational: same calm ink-70 body text weight,
          no warning styling, no urgency, no "you missed something" framing.
          Renders only when outcome === "skipped" AND we successfully fetched a
          non-zero category count. Anyone who completed the tour or hasn't
          seen it yet gets nothing. Clicking the inline "Review setup options"
          link re-opens the same walkthrough. */}
      {c.onboarding_walkthrough_outcome === "skipped" && vendorCatCount > 0 && (
        <div
          data-testid="dash-skipped-tour-nudge"
          className="mt-6 text-sm text-ink/70 leading-snug"
        >
          You skipped the tour. <span className="tabular-nums font-semibold text-ink">{vendorCatCount}</span> vendor {vendorCatCount === 1 ? "option is" : "options are"} still available under Nikshala Partner Services below.
          <button
            type="button"
            data-testid="dash-skipped-tour-nudge-reopen"
            onClick={() => setOnboardingOpen(true)}
            className="ml-2 underline underline-offset-2 text-elec hover:text-ink font-semibold"
          >
            Review setup options
          </button>
        </div>
      )}

      {/* iter84 - Cost of Living entry point. Small, calm link. The full
          product surface lives at /dashboard/cost-estimator. */}
      <div className="mt-4">
        <Link
          to="/dashboard/cost-estimator"
          data-testid="dash-cost-estimator-link"
          className="inline-flex items-center gap-2 text-sm font-semibold text-ink hover:text-elec transition-colors"
        >
          <Coins size={14} strokeWidth={2.2} className="text-elec"/>
          What a month in {c.host_city || "your host city"} actually costs
          <ArrowRight size={13} className="text-elec"/>
        </Link>
      </div>

      {/* iter93 (Item GG · P1) — "More tools" secondary strip. Packing +
         Concierge live here rather than in the top-fold product strip because
         real analytics (30d) show they get ~1% of the traffic Manifest and
         Roam get. Fully functional, one click away, same visual family — just
         no longer visually competing for attention with the high-demand
         products. Compass gets its own full section below the fold as before
         (line 376) because it renders the country checklist + booking portal
         which needs real vertical space. */}
      <div className="mt-8" data-testid="dash-more-tools">
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted font-bold">More tools</div>
        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Link
            to="/dashboard/packing"
            data-testid="dash-more-packing"
            className="moe-card p-4 hover:border-elec transition-colors group flex items-start gap-3"
          >
            <div className="w-8 h-8 rounded-full bg-elec/10 text-elec flex items-center justify-center shrink-0">
              <Package size={14} strokeWidth={2}/>
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-bold text-ink leading-tight">Smart Packing</div>
              <div className="mt-0.5 text-[11.5px] text-muted leading-snug">
                {packing?.profile_complete
                  ? <>{packing.readiness_pct}% ready · {packing.essential_remaining} essential left</>
                  : <>Answer 6 questions to generate your list.</>}
              </div>
            </div>
            <ArrowRight size={13} className="text-elec transition-transform group-hover:translate-x-0.5 shrink-0"/>
          </Link>
          <Link
            to="/pricing#tier-concierge"
            data-testid="dash-more-concierge"
            className="moe-card p-4 hover:border-elec transition-colors group flex items-start gap-3"
          >
            <div className="w-8 h-8 rounded-full bg-elec/10 text-elec flex items-center justify-center shrink-0">
              <LifeBuoy size={14} strokeWidth={2}/>
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-bold text-ink leading-tight">Concierge</div>
              <div className="mt-0.5 text-[11.5px] text-muted leading-snug">
                A human who helps when something's stuck.
              </div>
            </div>
            <ArrowRight size={13} className="text-elec transition-transform group-hover:translate-x-0.5 shrink-0"/>
          </Link>
        </div>
      </div>

      {/* iter88.b (Item AA · REBUILD) — Dashboard overview.
          First bento pass was rejected on real usability grounds (size
          followed decoration, not content). This rebuild applies the
          founder's diagnosis: one focal point (next deadline), tight
          Gestalt grouping of the timeline cluster, compact underweighted
          quick-jumps for unrelated shortcuts. See
          /app/frontend/src/components/DashboardOverview.jsx for the
          rationale per section. */}
      <DashboardOverview
        c={c}
        tasks={tasks}
        manifest={manifest}
        packing={packing}
      />

      {/* SPECIAL FLAGS */}
      {playbook?.recent_flag && (
        <div className="mt-6 moe-panel-yolk p-4 flex items-start gap-3" data-testid="dash-recent-flag">
          <AlertTriangle size={20} className="text-ink shrink-0 mt-0.5"/>
          <div className="text-sm text-ink leading-snug">{playbook.recent_flag}</div>
        </div>
      )}
      {playbook?.special_flag === "accommodation_required" && (
        <div className="mt-6 moe-panel-yolk p-4 flex items-start gap-3">
          <AlertTriangle size={20} className="text-ink shrink-0 mt-0.5"/>
          <div className="text-sm text-ink"><b>Austria specific:</b> confirmed accommodation is required for the visa application itself.</div>
        </div>
      )}
      {playbook?.special_flag === "host_university_files" && (
        <div className="mt-6 moe-panel-pink p-4 flex items-start gap-3">
          <AlertTriangle size={20} className="text-ink shrink-0 mt-0.5"/>
          <div className="text-sm text-ink"><b>Netherlands specific:</b> your host university files the MVV/VVR, not you. Coordinate early.</div>
        </div>
      )}

      {/* OFFICIAL CHECKLIST + BOOKING */}
      {(playbook?.official_checklist?.length > 0 || playbook?.booking_url) && (
        <>
          <div className="mt-12" data-testid="dash-compass-framing">
            <div className="text-[10px] uppercase tracking-[0.2em] text-elec font-bold">Northstar</div>
            <div className="mt-1 text-lg md:text-xl font-bold text-ink leading-snug">Your visa and arrival path</div>
            <div className="mt-0.5 text-[13px] text-ink/70">
              The official {c.country} checklist plus your appointment booking portal. Sourced from the country's immigration site, not blog posts.
            </div>
          </div>
          <div className="mt-4 grid md:grid-cols-3 gap-5">
          <div className="md:col-span-2 moe-card p-6">
            <div className="flex items-baseline justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest text-elec font-bold">Official {c.country} checklist</div>
                <div className="text-sm text-muted mt-1">From {playbook.source_url?.replace(/^https?:\/\//,"").split("/")[0] || "the country's immigration site"}</div>
              </div>
              {playbook?.source_url && (
                <a href={playbook.source_url} target="_blank" rel="noreferrer" className="text-xs font-semibold text-elec hover:underline hidden sm:inline">Source ↗</a>
              )}
            </div>
            <ul className="mt-5 grid md:grid-cols-2 gap-x-8 gap-y-3">
              {(playbook.official_checklist || []).map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-[14px]">
                  <span className="mt-1 w-4 h-4 rounded-full border border-black flex items-center justify-center shrink-0">
                    <FileCheck size={9} strokeWidth={3} className="text-ink"/>
                  </span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="moe-panel-yolk p-6">
            <div className="text-xs uppercase tracking-widest font-bold">Book appointment</div>
            <div className="mt-3 text-sm font-semibold">{playbook?.processing_time || "Check portal for current wait"}</div>
            <div className="mt-1 text-xs">{playbook?.apply_before_days ? `Apply ~${playbook.apply_before_days} days before travel` : ""}</div>
            {playbook?.booking_url && (
              <a href={playbook.booking_url} target="_blank" rel="noreferrer"
                 className="mt-5 inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-ink text-white font-semibold text-sm hover:opacity-90">
                Open booking portal →
              </a>
            )}
            <div className="mt-4 text-[11px] text-ink/70">Fee: {playbook?.visa_fee}</div>
            <InrAmount raw={playbook?.visa_fee} size="xs" showRateLine={false} className="text-white/85" />
          </div>
        </div>
        </>
      )}

      {/* Crowd-sourced wait-time report prompt. Only shown if eligible */}
      <WaitTimeReportPrompt />

      {/* TASKS · framing added iter71: "what this is for" preface so a first-
          time visitor understands this is the daily-driver checklist before
          scanning three dense stage panels. Panel content itself is unchanged. */}
      <div className="mt-12" id="dash-tasks-framing" data-testid="dash-tasks-framing">
        <div className="text-[10px] uppercase tracking-[0.2em] text-elec font-bold">Your timeline</div>
        <div className="mt-1 text-lg md:text-xl font-bold text-ink leading-snug">
          Personalised task list, grouped by stage
        </div>
        <div className="mt-0.5 text-[13px] text-ink/70">
          Every task carries a real deadline based on your start date and duration. Check items off as you complete them. This is your daily driver.
        </div>
      </div>
      <div className="mt-4 grid lg:grid-cols-3 gap-5 lg:gap-6">
        {["pre_departure", "arrival", "settling"].map((stage) => {
          const list = grouped[stage] || [];
          const meta = STAGE_LABEL[stage];
          return (
            <div key={stage} className={`${meta.panel} p-6`}>
              <div className="flex items-baseline justify-between mb-4">
                <div>
                  <div className="text-xs uppercase tracking-widest font-bold">{meta.label}</div>
                  <div className="text-xs text-ink/70 mt-1">{list.filter(t=>t.status==="completed").length}/{list.length} complete</div>
                </div>
              </div>
              {stage === "pre_departure" && packing && (
                <Link to="/dashboard/packing" data-testid="dash-packing-task-banner" className="mb-4 block rounded-xl bg-white/90 hover:bg-white transition-colors p-3.5 border border-ink/10">
                  <div className="flex items-center gap-2.5">
                    <Package size={16} className="text-elec shrink-0" strokeWidth={2}/>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-bold text-ink">Complete your smart packing checklist</div>
                      <div className="mt-0.5 text-[11px] text-ink/70">
                        {packing.profile_complete
                          ? <>{packing.readiness_pct}% ready · <span className="text-ink">{packing.essential_remaining} essential left</span>{packing.restricted_unresolved > 0 && <> · <span className="text-[color:var(--moe-pink)]">{packing.restricted_unresolved} to verify</span></>}{packing.days_until_departure != null && <> · <span className="text-muted">{packing.days_until_departure} days to go</span></>}</>
                          : "Answer 6 quick questions. We'll generate a personalised list for your exact trip."}
                      </div>
                    </div>
                    <ArrowRight size={14} className="shrink-0 text-elec"/>
                  </div>
                </Link>
              )}
              <ul className="space-y-3 moe-stagger">
                {list.map(t => {
                  const Icon = CAT_ICON[t.category] || FileCheck;
                  const done = t.status === "completed";
                  return (
                    <li key={t.id} data-testid={TID.taskItem(t.id)} className="flex items-start gap-3 p-3 rounded-lg bg-white/70 hover:bg-white transition-colors">
                      <TaskCheck done={done} onClick={() => toggle(t)} testid={TID.taskToggle(t.id)}/>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start gap-2">
                          <Icon size={14} className="text-ink/70 mt-0.5 shrink-0" strokeWidth={1.8}/>
                          <div className={`text-sm ${done ? "text-ink/60 line-through" : "text-ink"}`}>{t.task_name}</div>
                        </div>
                        <div className="mt-1 ml-6"><DeadlineBadge isoDate={t.deadline_date}/></div>
                      </div>
                    </li>
                  );
                })}
                {list.length === 0 && <li className="text-sm text-ink/70">No tasks in this stage.</li>}
              </ul>
              {/* iter76 (Item D) — Cross-link into Manifest. Compass tasks
                 are personal-track; peers in your same cohort are working
                 the same list. Shown only when the user is opted-in AND has
                 at least one match (data-backed, never fabricated). Deep
                 links straight into the Matches tab, not the generic
                 Manifest home. */}
              {manifest?.opted_in && manifest?.matches_count > 0 && list.length > 0 && (() => {
                // iter76 (Item D) — Prefer the same_university count when it
                // exists so this link matches what the user lands on in the
                // Matches strip (which surfaces same_university tier first).
                // Falls back to matches_count when no same-uni peers exist so
                // the link still surfaces cohort activity honestly.
                const n = manifest.same_university_count > 0
                  ? manifest.same_university_count
                  : manifest.matches_count;
                const scope = manifest.same_university_count > 0
                  ? "at your host uni" : "in your cohort";
                return (
                  <Link
                    to="/dashboard/manifest?tab=matches"
                    data-testid={`dash-tasks-manifest-link-${stage}`}
                    className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-semibold text-elec hover:underline"
                  >
                    <Users size={11} strokeWidth={2.4}/>
                    Coordinate with the {n} peer{n === 1 ? "" : "s"} {scope}
                    <ArrowRight size={11}/>
                  </Link>
                );
              })()}
            </div>
          );
        })}
      </div>

      {/* ==================== Personalised country body ====================
          Same source of truth as /countries/{country}. `hostCity` lights up the
          matching registration office (Germany-style) or falls back to the
          national portal (France-style).
          `compactMerged` collapses the four separate reference sections into
          the single tabbed <LocalLifeTabs> panel. Task-actionable content
          lives in the board above; this panel is reference-only.

          iter71. Framing header added so the visitor knows this panel is
          reference material for local life, not another checklist. Copy is
          neutral across personas; the panel content stays identical. */}
      <div className="mt-10" data-testid="dash-locallife-framing">
        <div className="text-[10px] uppercase tracking-[0.2em] text-elec font-bold">Local life</div>
        <div className="mt-1 text-lg md:text-xl font-bold text-ink leading-snug">
          {c.country} reference. Transit, connectivity, banking, and services
        </div>
        <div className="mt-0.5 text-[13px] text-ink/70">
          Read-only reference for after you land. Anything actionable already lives in your task board above.
        </div>
      </div>
      <div data-testid="dash-country-body" className="mt-3">
        <CountryPostArrival country={c.country} hostCity={c.host_city} dashboardMode compactMerged />
      </div>

      {/* iter55. Review setup options: honest re-entry point for the guided
          walkthrough. Available at every visit (before AND after the first-run
          modal), so anyone who skipped can revisit without hunting for the
          option. Re-opening does NOT clear the seen flag. The modal never
          auto-fires again once dismissed. */}
      <div className="mt-6 text-center">
        <button
          type="button"
          data-testid="dash-review-setup-options"
          onClick={() => setOnboardingOpen(true)}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink/70 hover:text-elec"
        >
          <Sparkles size={14} className="text-elec"/> Review setup options
        </button>
        <div className="mt-1 text-[11px] text-muted">
          Optional tour of vendor and partner services in scope for your case.
        </div>
      </div>

      {/* iter86 - Compact "Delete my account" section. Founder feedback
          collapsed the standalone Settings page into a low-visual-weight
          Dashboard footer disclosure. All delete logic preserved. */}
      <DeleteAccountSection />

      {/* iter86 (Item W) - Notification sound preference. Lives just above
          the Delete section so all "account plumbing" is in one calm strip. */}
      <NotificationPrefsToggle />

      <OnboardingWalkthrough
        open={onboardingOpen}
        onClose={() => {
          setOnboardingOpen(false);
          // Refresh case doc so `onboarding_walkthrough_outcome` (set by the
          // POST inside the modal's close path) is reflected here — this is
          // what surfaces the quiet nudge line for a Skip outcome.
          load();
        }}
        country={c.country}
        hostCity={c.host_city}
        caseId={c.id}
      />

      {/* iter71. Persona questionnaire modal. Auto-opens on first visit if
          persona is null AND persona_skipped is false. Once it closes, we
          reload the case so the reordering and framing pick up the new
          persona. If the user hadn't seen the onboarding walkthrough yet
          (first-visit path), fire it right after the persona modal closes so
          both first-run overlays chain in sequence. */}
      <PersonaQuestionnaire
        open={personaOpen}
        initialPersona={c.persona}
        onClose={({ skipped, persona: newPersona }) => {
          setPersonaOpen(false);
          load();
          // iter75 (Item B) — chain into product tour on first-time visit.
          // Vendor walkthrough is now on-demand only (via footer link) so it
          // is no longer auto-fired here.
          if (c.product_tour_seen === false) {
            setTimeout(() => setProductTourOpen(true), 200);
          }
        }}
      />

      {/* iter75 (Item B) — First-time-user product tour. Auto-opens after
          persona resolves (see chain rule above). Skippable at every step
          via X, Esc, backdrop click, or the explicit Skip button. Persists
          product_tour_seen=true on ANY close outcome so it never re-prompts. */}
      <ProductTour
        open={productTourOpen}
        country={c.country}
        onClose={() => {
          setProductTourOpen(false);
          load();
        }}
      />
    </div>
  );
}
